import {
  HttpError,
  HTTPErrors,
  isUnauthorizedReason,
  type UnauthorizedReason,
} from './HTTPErrors';

/**
 * User-facing copy for each {@link UnauthorizedReason}.
 *
 * - `message` is the short headline shown in error UI / API responses.
 * - `details` explains *why* the user is seeing the error, so they understand
 *   exactly what happened (e.g. their session expired) rather than a cryptic
 *   "Unauthorized".
 */
export const UNAUTHORIZED_REASON_COPY: Record<
  UnauthorizedReason,
  { readonly message: string; readonly details: string }
> = {
  'no-session': {
    message: 'You are not signed in.',
    details:
      'We could not find an active session for your account. Please sign in to continue.',
  },
  'session-expired': {
    message: 'Your session has expired.',
    details:
      'For your security, sessions automatically expire after a period of time. Please sign in again to continue where you left off.',
  },
  'invalid-session': {
    message: 'Your session is no longer valid.',
    details:
      'Your session could not be verified. This can happen after a password change, a sign-out on another device, or an account update. Please sign in again.',
  },
  'invalid-token': {
    message: 'The provided access token is invalid or has expired.',
    details:
      'The integration token used for this request could not be verified. Please refresh the token and try again.',
  },
  unknown: {
    message: 'You are not authorized to perform this action.',
    details:
      'We could not verify your access for this request. Please sign in again, and contact support if the problem continues.',
  },
};

/**
 * Builds a 401 {@link HttpError} carrying a machine-readable {@link UnauthorizedReason}
 * and a user-friendly message/details for that reason.
 */
export function createUnauthorizedError(
  reason: UnauthorizedReason,
  options?: { readonly requestId?: string },
): HttpError {
  const copy = UNAUTHORIZED_REASON_COPY[reason];
  return HTTPErrors.unauthorized(copy.message, 'Unauthorized', {
    reason,
    ...(options?.requestId ? { requestId: options.requestId } : {}),
  });
}

const SESSION_EXPIRED_PATTERNS = [
  'session has expired',
  'session expired',
  'jwt expired',
  'token expired',
  'expired',
] as const;

/**
 * Extracts the {@link UnauthorizedReason} from an error.
 *
 * Prefers an explicit `reason` field (set by the server), then falls back to
 * inspecting the message so legacy/plain 401s still resolve to a useful reason.
 * Returns `null` when the error is not an unauthorized error at all.
 */
export function getUnauthorizedReason(
  error: unknown,
): UnauthorizedReason | null {
  const explicit = readExplicitReason(error);
  if (explicit) {
    return explicit;
  }

  if (!isUnauthorizedLike(error)) {
    return null;
  }

  const message = readErrorMessage(error).toLowerCase();
  if (SESSION_EXPIRED_PATTERNS.some((pattern) => message.includes(pattern))) {
    return 'session-expired';
  }

  return 'unknown';
}

function readExplicitReason(error: unknown): UnauthorizedReason | null {
  if (error instanceof HttpError && isUnauthorizedReason(error.reason)) {
    return error.reason;
  }
  if (error !== null && typeof error === 'object') {
    const reason = (error as Record<string, unknown>)['reason'];
    if (isUnauthorizedReason(reason)) {
      return reason;
    }
  }
  return null;
}

function isUnauthorizedLike(error: unknown): boolean {
  if (error instanceof HttpError) {
    return error.code === 401 || error.name === 'Unauthorized';
  }
  if (error !== null && typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    return obj['code'] === 401 || obj['name'] === 'Unauthorized';
  }
  return false;
}

function readErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (error !== null && typeof error === 'object') {
    const message = (error as Record<string, unknown>)['message'];
    return typeof message === 'string' ? message : '';
  }
  return typeof error === 'string' ? error : '';
}
